/**
 * Runtime Environment Configuration
 *
 * Configuration is determined by the current URL/hostname.
 * Build once, deploy everywhere - no process.env needed!
 *
 * Hostname configurations are stored in config_hostnames.json
 *
 * Usage:
 * import { ENVIRONMENT, APP_VERSION, WHITELABEL_COMMUNITY_ID } from 'config_environment';
 */

import HOSTNAME_CONFIG from './config_hostnames.json';

// ============================================================================
// Configuration by Hostname
// ============================================================================

type EnvironmentConfig = {
	title?: string;
	environment: string;
	appVersion: string;
	apiBaseUrl: string;
	whitelabel?: {
		communityId: number;
		communitySlug: string;
		loginPageUrl?: string;
		ssoEnabled: boolean;
	};
	integrations: {
		stripePublishableKey: string;
		agoraAppToken: string;
		rollbarAccessToken: string;
		rewardfulKey?: string;
	};
};

// ============================================================================
// Get Current Configuration
// ============================================================================

const getCurrentHostname = (): string => {
	if (typeof window === 'undefined') return 'localhost';
	return window.location.hostname;
};

const getCurrentConfig = (): EnvironmentConfig => {
	const hostname = getCurrentHostname();
	const config = (HOSTNAME_CONFIG as Record<string, EnvironmentConfig>)[hostname];

	if (!config) {
		console.warn(`No configuration found for hostname: ${hostname}, falling back to localhost config`);
		return (HOSTNAME_CONFIG as Record<string, EnvironmentConfig>)['localhost'];
	}

	return config;
};

// Get the current config based on hostname
const currentConfig = getCurrentConfig();

// ============================================================================
// Exported Variables (for backward compatibility)
// ============================================================================

export const NODE_ENV = process.env.NODE_ENV || 'development';
export const APP_TITLE = currentConfig.title || 'Ugenie';
export const ENVIRONMENT = currentConfig.environment;
export const APP_VERSION = currentConfig.appVersion;
export const PUBLIC_URL = process.env.PUBLIC_URL || '';

// Backend API
export const API_BASE_URL = currentConfig.apiBaseUrl;

// Whitelabel Configuration
export const WHITELABEL_COMMUNITY_ID = currentConfig.whitelabel?.communityId;
export const WHITELABEL_COMMUNITY_SLUG = currentConfig.whitelabel?.communitySlug;
export const WHITELABEL_LOGIN_PAGE = currentConfig.whitelabel?.loginPageUrl;
export const SSO_ENABLED = currentConfig.whitelabel?.ssoEnabled || false;

// Third-party Integrations
export const STRIPE_PUBLISHABLE_KEY = currentConfig.integrations.stripePublishableKey;
export const AGORA_APP_TOKEN = currentConfig.integrations.agoraAppToken;
export const ROLLBAR_ACCESS_TOKEN = currentConfig.integrations.rollbarAccessToken;
export const REWARDFUL_KEY = currentConfig.integrations.rewardfulKey;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the full configuration object
 */
export const getConfig = (): EnvironmentConfig => currentConfig;

/**
 * Get current hostname
 */
export const getHostname = (): string => getCurrentHostname();

/**
 * Check if current site is a whitelabel
 */
export const isWhitelabel = (): boolean => !!currentConfig.whitelabel;

/**
 * Add or update configuration for a hostname (useful for testing)
 */
export const addHostnameConfig = (hostname: string, config: EnvironmentConfig): void => {
	(HOSTNAME_CONFIG as Record<string, EnvironmentConfig>)[hostname] = config;
};
